import type { PropsWithChildren } from "react";

const ZUP_IT_BASE_URL = "https://portal.zuplo.com/zup-it";

/** The Zuplo "Z" mark, lifted from `public/zuplo-dark.svg`. */
function ZuploMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="-1 0 39 32"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M27.6959 20.4316H16.9592L28.398 9.19958C28.5057 9.09364 28.579 8.95884 28.6087 8.81216C28.6384 8.66547 28.6232 8.51345 28.5649 8.37523C28.5067 8.23701 28.408 8.11876 28.2814 8.03536C28.1547 7.95197 28.0057 7.90716 27.8531 7.90658H9.68164V0.453578H29.6143C31.0778 0.435898 32.5163 0.826043 33.7623 1.57858C35.0087 2.33191 36.012 3.41571 36.6561 4.70458C37.3215 6.08421 37.5354 7.63169 37.2685 9.13507C37.0017 10.6385 36.267 12.0242 35.1653 13.1026L27.6959 20.4306V20.4316Z" />
      <path d="M9.68263 11.4959H20.4214L9.00304 22.7059C8.8937 22.8138 8.81931 22.9509 8.78919 23.1C8.75908 23.2492 8.77459 23.4038 8.83377 23.5443C8.89295 23.6849 8.99317 23.8052 9.12186 23.8902C9.25054 23.9751 9.40196 24.0209 9.55712 24.0219H27.6785V31.4739H7.76528C6.3018 31.4915 4.8633 31.1014 3.61732 30.3489C2.37111 29.5951 1.36814 28.511 0.724466 27.2219C0.0594431 25.8423 -0.154164 24.2949 0.112896 22.7917C0.379956 21.2886 1.11463 19.903 2.2163 18.8249L9.68263 11.4959Z" />
    </svg>
  );
}

function GithubMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2C6.477 2 2 6.463 2 11.97c0 4.404 2.865 8.14 6.839 9.458.5.092.682-.216.682-.48 0-.236-.008-.864-.013-1.695-2.782.602-3.369-1.337-3.369-1.337-.454-1.151-1.11-1.458-1.11-1.458-.908-.618.069-.606.069-.606 1.003.07 1.531 1.027 1.531 1.027.892 1.524 2.341 1.084 2.91.828.092-.643.35-1.083.636-1.332-2.22-.251-4.555-1.107-4.555-4.927 0-1.088.39-1.979 1.029-2.675-.103-.252-.446-1.266.098-2.638 0 0 .84-.268 2.75 1.022A9.606 9.606 0 0112 6.82c.85.004 1.705.114 2.504.336 1.909-1.29 2.747-1.022 2.747-1.022.546 1.372.202 2.386.1 2.638.64.696 1.028 1.587 1.028 2.675 0 3.83-2.339 4.673-4.566 4.92.359.307.678.915.678 1.846 0 1.332-.012 2.407-.012 2.734 0 .267.18.577.688.48C19.137 20.107 22 16.373 22 11.969 22 6.463 17.522 2 12 2z"
      />
    </svg>
  );
}

/**
 * A self-contained, Zuplo-branded "Deploy to Zuplo" callout: an explanation and
 * a link to the source repository on the left, a custom deploy button on the
 * right. The button opens the Zuplo portal, which clones `repoUrl` into the
 * reader's account, then builds and deploys it — no local setup required.
 *
 * Registered globally in `src/components.tsx`, so it works in any MDX file
 * without an import:
 *
 * ```mdx
 * <ZupIt repoUrl="https://github.com/zuplo-samples/graphql" />
 * ```
 *
 * Pass `children` to override the default description, and `title` to change the
 * heading. The GitHub link is derived from `repoUrl` automatically.
 */
export default function ZupIt({
  repoUrl,
  title = "Deploy an example",
  children,
}: PropsWithChildren<{ repoUrl: string; title?: string }>) {
  const deployUrl = `${ZUP_IT_BASE_URL}?sourceRepoUrl=${encodeURIComponent(
    repoUrl,
  )}`;
  const repoLabel = repoUrl.replace(/^https?:\/\/(www\.)?github\.com\//, "");

  return (
    <div className="not-prose my-6 flex flex-col gap-5 rounded-xl border border-[#ff00bd]/20 bg-[#ff00bd]/5 p-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="flex flex-col gap-2">
        <span className="text-base font-semibold text-foreground">{title}</span>
        <div className="text-sm leading-relaxed text-muted-foreground [&>p]:m-0">
          {children ?? (
            <p>
              Spin up a working copy of this project in your own Zuplo account —
              no setup required.
            </p>
          )}
        </div>
        <a
          href={repoUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex w-fit items-center gap-1.5 rounded-full border border-border px-3 py-1 text-sm font-medium text-foreground no-underline transition-colors hover:border-[#ff00bd]/40 hover:text-[#ff00bd]"
        >
          <GithubMark className="h-3.5 w-3.5" />
          {repoLabel}
        </a>
      </div>
      <a
        href={deployUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex shrink-0 self-start rounded-lg bg-[#ff00bd] px-5 py-2.5 no-underline shadow-[0_4px_16px_-4px_rgba(255,0,189,0.5)] transition-all duration-150 hover:-translate-y-0.5 hover:bg-[#e6009f] hover:shadow-[0_8px_24px_-4px_rgba(255,0,189,0.65)] sm:self-auto"
      >
        {/* Color lives on this span, not the <a>. The docs site ships a global
            `.prose a:hover { color: var(--primary) }` rule with no not-prose
            exclusion, and it out-specifies a `hover:text-white` utility — so it
            would repaint the label pink-on-pink. A color set directly on a child
            always beats an inherited one, sidestepping the specificity fight. */}
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-white">
          <ZuploMark className="h-[18px] w-auto" />
          Deploy to Zuplo
        </span>
      </a>
    </div>
  );
}
